import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { CartLine, SalesLine, SimpleProduct, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ProductCategoryResult } from '@relewise/client';
import { ICartlinesViewProps } from '../../../modules/ext-cart/components/ext-cart-line-items';
import { formatPrice } from '../../../utils/helper';
import { RelewiseLineItem, RelewiseCart, RelewiseOrder } from '../types/tracking';
import { AutosuggestSearchResult } from './SearchService';
import { MappedProduct, SearchSuggestions } from '../types/mapped';
import { RelewiseProductResult } from '../types/relewiseData';

const DIMENSION_TYPE_VALUES_MAP = {
    1: 'color',
    3: 'size'
};
interface VariantDimensions {
    color?: string;
    size?: string;
}
interface LineItemInput {
    product: SimpleProduct | undefined;
    lineData: CartLine | SalesLine | undefined;
}

interface InitializedService {
    currencyId: string;
    currency: string;
    locale: string;
    legalEntityId: string;
    environmentName: string;
    sitePath: string;
    disabled: false;
}

export class RelewiseMappingService {
    protected currencyId: string | undefined;
    protected currency: string | undefined;
    protected locale: string | undefined;
    protected legalEntityId: string | undefined;
    protected environmentName: string | undefined;
    protected sitePath: string | undefined;
    protected disabled = true;

    constructor(request: IRequestContext) {
        this.environmentName = request.app?.config.environmentName;
        if (!this.environmentName) {
            console.error(
                '[Relewise Mapping Service] Cannot initialize service: Missing Relewise configuration field environmentName. Add this config in the CMS.'
            );
            return;
        }

        this.legalEntityId = request.channel?.InventLocationDataAreaId;
        if (!this.legalEntityId) {
            console.error('[Relewise Mapping Service] Cannot initialize service: Legal Entity ID is not configured.');
            return;
        }

        this.currency = request.channel?.Currency;
        if (!this.currency) {
            console.error('[Relewise Mapping Service] Cannot initialize service: Currency is not configured.');
            return;
        }
        this.currencyId = `${this.currency}_${this.legalEntityId}`;

        this.locale = request.locale;
        if (!this.locale) {
            console.error('[Relewise Mapping Service] Cannot initialize service: Locale is not configured.');
            return;
        }

        this.sitePath = request.sitePath;
        if (!this.sitePath) {
            console.error('[Relewise Mapping Service] Cannot initialize service: Site path is not configured.');
            return;
        }

        this.disabled = false;
    }

    public getCurrencyId() {
        return this.currencyId;
    }

    private isEnabled(): this is InitializedService {
        return !this.disabled;
    }

    public prefixId(itemId: string): string;
    public prefixId(itemId: string | undefined): string | undefined;
    public prefixId(itemId: string | undefined) {
        if (!this.isEnabled() || !itemId) {
            return;
        }
        const envName = this.environmentName;

        return `${envName}|${itemId}`;
    }

    public unprefixId(prefixedId: string | undefined): string | undefined {
        if (!prefixedId) {
            return;
        }
        const parts = prefixedId.split('|');
        return !!parts.length ? parts.pop() ?? '' : prefixedId;
    }

    public mapProductDimensionsToRelewiseVariantId(itemId: string, color: string, size: string) {
        const normalizedColor = color.replace(/ /g, '_').replace(/#/g, '');
        const normalizedSize = size.replace(/ /g, '_').replace(/\//g, '');

        return `${this.prefixId(itemId)}${normalizedColor}${normalizedSize}`;
    }

    public mapToRelewiseCart(subtotalAmount: number, cartLines: ICartlinesViewProps[] | undefined): RelewiseCart {
        const lineItems =
            cartLines?.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.cartline,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: subtotalAmount, currency: this.currencyId ?? '' },
            lineItems
        };
    }

    public mapToRelewiseOrder(salesLines: ISalesLine[], salesOrder: SalesOrder): RelewiseOrder {
        const lineItems =
            salesLines.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.salesLine,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: salesOrder.SubtotalAmount ?? 0, currency: this.currencyId ?? '' },
            lineItems,
            orderNumber: salesOrder.Id
        };
    }

    private mapToRelewiseLineItem({ lineData, product }: LineItemInput): RelewiseLineItem {
        const variantId = this.mapSimpleProductToRelewiseVariantId(product);
        return {
            lineTotal: lineData?.TotalAmount ?? 0,
            productId: this.prefixId(lineData?.ItemId) ?? '',
            quantity: lineData?.Quantity ?? 0,
            ...(variantId ? { variantId } : {})
        };
    }

    public mapSimpleProductToRelewiseVariantId(product: SimpleProduct | undefined): string | undefined {
        if (!product?.ItemId) {
            return;
        }

        const variantDimensions = product.Dimensions?.reduce<VariantDimensions>((acc, val) => {
            const dimensionName = DIMENSION_TYPE_VALUES_MAP[val.DimensionTypeValue];
            if (dimensionName) {
                acc[dimensionName] = val.DimensionValue?.Value;
            }
            return acc;
        }, {});

        const { color, size } = variantDimensions ?? {};
        if (!color || !size) {
            return;
        }

        return this.mapProductDimensionsToRelewiseVariantId(product.ItemId, color, size);
    }

    public mapRelewiseProductResultList(products: RelewiseProductResult[] | undefined): MappedProduct[] {
        if (!products) {
            return [];
        }
        return products.map(p => this.mapRelewiseProductResult(p)).filter((p): p is MappedProduct => !!p);
    }

    public mapRelewiseProductResult(product: RelewiseProductResult): MappedProduct | undefined {
        if (!this.isEnabled()) {
            return;
        }
        const basePriceFormatted = formatPrice(product.variant?.listPrice?.toString(), this.currency, this.locale);
        const finalPriceFormatted = formatPrice(product.variant?.salesPrice?.toString(), this.currency, this.locale);
        return {
            id: product.variant?.variantId ?? undefined,
            displayName: product.variant?.displayName ?? '',
            imgUrl: product.variant?.data.PrimaryImageUrl.value ?? '',
            basePriceFormatted,
            finalPriceFormatted,
            hasDiscount: product.variant?.listPrice !== product.variant?.salesPrice,
            productPageUrl: `${this.sitePath}${product.variant?.data.RelativeUrl.value ?? ''}`,
            colors: product.data.Colors.value.$values.map(c => ({ name: c.data.Name.value, hex: c.data.Hex.value }))
        };
    }

    public mapAutosuggestedSearchResult(result: AutosuggestSearchResult | undefined): SearchSuggestions | undefined {
        if (!this.isEnabled() || !result) {
            return;
        }

        return {
            categories: result.categories?.map(category => ({
                id: category.categoryId ?? '',
                label: category.displayName ?? '',
                url: this.buildCategoryPlpUrl(category) ?? ''
            })),
            keywords: result.searchTermPredictions?.map(prediction => ({
                id: prediction.term ?? '',
                label: prediction.term ?? '',
                url: `${this.sitePath}/search?q=${encodeURIComponent(prediction.term ?? '')}`
            })),
            products: this.mapRelewiseProductResultList(result.products)
        };
    }

    private buildCategoryPlpUrl(category: ProductCategoryResult): string | undefined {
        if (!this.isEnabled() || !category) {
            return;
        }

        const firstPath = category?.paths?.[0]?.pathFromRoot;
        const recordId = category.data?.RecordId?.value;
        if (!firstPath?.length || !recordId) {
            return;
        }

        const url = `${this.sitePath}/${firstPath
            .filter(s => !!s.id)
            .map(s => this.normalizeCategoryPathStepForUrl(s.id))
            .join('/')}/${recordId}.c`;

        return url;
    }

    private normalizeCategoryPathStepForUrl(stepId: string | null | undefined): string | undefined {
        if (!stepId) {
            return;
        }
        const unprefixedId = this.unprefixId(stepId);
        return encodeURIComponent(unprefixedId?.toLocaleLowerCase().replace(/ /g, '-') ?? '');
    }
}
